import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { FaFileAlt, FaPlus, FaBox } from "react-icons/fa";
import "../style/ReceivedChallan.css";
import api from "../api/axiosInstance";
import axios from "axios";
import { TokenManager } from "../utils/tokenManager";
import InsertProduct from "./InsertProduct";
import { getAllGoods } from "../api/goodsApi";
import { searchCustomers } from "../api/customerApi";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";

const productsList = ["Product A", "Product B", "Product C", "Product D"];

function ReceivedChallan() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showProductForm, setShowProductForm] = useState(false);
  const [showExtendedProductForm, setShowExtendedProductForm] = useState(false);
  const extendedFormRef = React.useRef(null);
  const [productForm, setProductForm] = useState({
    product: "",
    delivered: "",
    received: "",
    goodsItemId: null,
  });

  const [form, setForm] = useState({
    challanNumber: "",
    customer: "",
    customerId: "",
    challanType: "Received Challan",
    date: new Date().toISOString().split("T")[0],
    siteLocation: "",
    vehicleNo: "",
    transporter: "",
    driverDetails: "",
    deliveryDetails: "",
    poNo: "",
    depositType: "Deposit",
    depositAmount: "",
    depositNarration: "",
    products: [],
  });

  // State for tracking field errors
  const [fieldErrors, setFieldErrors] = useState({
    customer: false,
    siteLocation: false,
    vehicleNo: false,
    transporter: false,
    products: false,
  });

  // Customer search state
  const [customers, setCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Goods state for quick add product
  const [goods, setGoods] = useState([]);
  const [goodsLoading, setGoodsLoading] = useState(true);

  // Product management state
  const [products, setProducts] = useState([]);
  const [savedProducts, setSavedProducts] = useState([]);
  const [pageType, setPageType] = useState("received");
  const [showProductsSection, setShowProductsSection] = useState(false);

  // Delivered challan data state
  const [deliveredChallans, setDeliveredChallans] = useState([]);
  const [deliveredChallansLoading, setDeliveredChallansLoading] = useState(false);

  // Customer inventory state
  const [customerInventory, setCustomerInventory] = useState([]);
  const [customerInventoryLoading, setCustomerInventoryLoading] = useState(false);

  // Fetch goods from API
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        setGoodsLoading(true);
        const response = await getAllGoods();
        console.log("ReceivedChallan - Goods API response:", response);
        setGoods(response.data?.data || response.data || []);
      } catch (error) {
        console.error("ReceivedChallan - Error fetching goods:", error);
        toast.error("Failed to load products");
      } finally {
        setGoodsLoading(false);
      }
    };
    fetchGoods();
  }, []);

  // Customer search functions
  const handleCustomerSearch = async (searchTerm) => {
    setCustomerSearchTerm(searchTerm);
    
    if (searchTerm.length < 2) {
      setCustomers([]);
      setShowCustomerDropdown(false);
      return;
    }

    try {
      const response = await searchCustomers({ name: searchTerm });
      console.log("ReceivedChallan - Customer search response:", response);
      setCustomers(response.data?.data || response.data || []);
      setShowCustomerDropdown(true);
    } catch (error) {
      console.error("ReceivedChallan - Error searching customers:", error);
      setCustomers([]);
      setShowCustomerDropdown(false);
    }
  };

  const handleCustomerSelect = async (customer) => {
    setForm(prev => ({
      ...prev,
      customer: customer.name || customer.customerName,
      customerId: customer.id
    }));
    setCustomerSearchTerm(''); // Clear search term to prevent showing saved info
    setShowCustomerDropdown(false);
    setCustomers([]);
    setShowProductsSection(false);
    setDeliveredChallans([]);
    
    // Fetch delivered challans for this customer
    fetchDeliveredChallans(customer.id);
    
    // Fetch customer inventory items
    await fetchCustomerInventory(customer.id);
  };

  // Fetch delivered challans for customer
  const fetchDeliveredChallans = async (customerId) => {
    if (!customerId) return;
    
    try {
      setDeliveredChallansLoading(true);
      const token = TokenManager.getToken();
      
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.CHALLANS.SEARCH}?customerId=${customerId}&challanType=DELIVERED`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const challans = data.content || data || [];
        setDeliveredChallans(challans);
        console.log("ReceivedChallan - Delivered challans for customer:", data);
        console.log("ReceivedChallan - Challans array:", challans);
        console.log("ReceivedChallan - Number of challans:", challans.length);
        
        // Debug: Log items structure
        challans.forEach((challan, index) => {
          console.log(`Challan ${index}:`, challan);
          if (challan.items) {
            console.log(`Challan ${index} items:`, challan.items);
            challan.items.forEach((item, itemIndex) => {
              console.log(`Challan ${index} Item ${itemIndex}:`, item);
            });
          }
        });
      } else {
        console.error("Failed to fetch delivered challans");
        setDeliveredChallans([]);
      }
    } catch (error) {
      console.error("Error fetching delivered challans:", error);
      setDeliveredChallans([]);
    } finally {
      setDeliveredChallansLoading(false);
    }
  };

  // Fetch customer inventory items for validation
  const fetchCustomerInventory = async (customerId) => {
    if (!customerId) return [];
    
    try {
      setCustomerInventoryLoading(true);
      const response = await api.get(`challans/getCustomersInventoryItems/${customerId}`);
      console.log("ReceivedChallan - Customer inventory:", response.data);
      setCustomerInventory(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching customer inventory:", error);
      setCustomerInventory([]);
      return [];
    } finally {
      setCustomerInventoryLoading(false);
    }
  };

  /* RECEIVE PRODUCTS FROM INSERT PRODUCT PAGE */
  useEffect(() => {
    if (location.state?.products) {
      const mapped = location.state.products.map((p) => ({
        name: p.product,
        qty:
          form.challanType?.includes("Delivered") || !form.challanType
            ? Number(p.delivered)
            : Number(p.received),
      }));

      setForm((prev) => ({
        ...prev,
        products: mapped,
      }));
    }
  }, [location.state, form.challanType]);

  /* Scroll to extended form when it opens */
  useEffect(() => {
    if (showExtendedProductForm && extendedFormRef.current) {
      setTimeout(() => {
        extendedFormRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [showExtendedProductForm]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleProductChange(e) {
    if (e.target.name === "product") {
      const selectedGood = goods.find(g => g.name === e.target.value);
      console.log("ReceivedChallan - Selected good:", selectedGood);
      setProductForm({ 
        ...productForm, 
        [e.target.name]: e.target.value,
        goodsItemId: selectedGood ? selectedGood.id : null
      });
    } else {
      setProductForm({ ...productForm, [e.target.name]: e.target.value });
    }
  }

  function handleAddProduct() {
    if (!productForm.product || (!productForm.delivered && !productForm.received)) {
      toast.error("Please fill product details");
      return;
    }

    const newProduct = {
      name: productForm.product,
      qty: form.challanType?.includes("Delivered") 
        ? Number(productForm.delivered) 
        : Number(productForm.received),
      goodsItemId: productForm.goodsItemId,
      type: "",
      srNo: [],
    };

    setForm(prev => ({
      ...prev,
      products: [...prev.products, newProduct]
    }));

    // Clear products field error when a product is added
    setFieldErrors(prev => ({ ...prev, products: false }));

    // Reset product form
    setProductForm({
      product: "",
      delivered: "",
      received: "",
      goodsItemId: null,
    });

    setShowProductForm(false);
    toast.success("Product added successfully!");
  }

  function handleRemoveProduct(index) {
    setForm(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
    toast.success("Product removed!");
  }

  function handleProductsAdded(products) {
    const mappedProducts = products.map(p => ({
      name: p.product,
      qty: form.challanType?.includes("Delivered") 
        ? Number(p.delivered) 
        : Number(p.received),
      goodsItemId: p.goodsItemId,
      type: p.type || "",
      srNo: p.srNo || [],
    }));

    setForm(prev => ({
      ...prev,
      products: [...prev.products, ...mappedProducts]
    }));

    setShowExtendedProductForm(false);
    toast.success(`${products.length} products added successfully!`);
  }

  async function handleSubmit(e) {
    console.log("ReceivedChallan - handleSubmit called");
    e.preventDefault();
    
    console.log("ReceivedChallan - Form data:", form);
    console.log("ReceivedChallan - Customer search term:", customerSearchTerm);
    console.log("ReceivedChallan - Products:", form.products);

    // Reset field errors
    const newFieldErrors = {
      customer: !form.customerId,
      siteLocation: !form.siteLocation,
      vehicleNo: !form.vehicleNo,
      transporter: !form.transporter,
      products: form.products.length === 0,
    };
    
    setFieldErrors(newFieldErrors);

    // Check for validation errors and build specific error message
    const errorMessages = [];
    if (newFieldErrors.customer) errorMessages.push("Customer");
    if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
    if (newFieldErrors.vehicleNo) errorMessages.push("Vehicle Number");
    if (newFieldErrors.transporter) errorMessages.push("Transporter");
    if (newFieldErrors.products) errorMessages.push("Products");

    if (errorMessages.length > 0) {
      console.log("ReceivedChallan - Validation failed - missing fields:", errorMessages);
      toast.error(`Please fill required fields: ${errorMessages.join(', ')}`);
      return;
    }

    const token = TokenManager.getToken();
    if (!token) {
      console.log("ReceivedChallan - Validation failed - no token");
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    const payload = {
      customerId: form.customerId,
      customerName: form.customer,
      siteLocation: form.siteLocation,
      vehicleNumber: form.vehicleNo,
      deliveredChallanNo: "",
      driverName: form.driverDetails,
      driverNumber: "",
      transporter: form.transporter,
      lrNumber: "",
      challanNumber: form.challanNumber === "AUTO" ? "" : form.challanNumber,
      challanType: "RECEIVED",
      date: form.date,
      purchaseOrderNo: form.poNo,
      returnedAmount:
        form.depositType === "Returned" ? Number(form.depositAmount) : 0,
      depositeNarration: form.depositNarration,
      deliveryDetails: form.deliveryDetails,

      items: form.products
        .filter((p) => (p.receivedQty || p.qty) > 0)
        .map((p, idx) => ({
          goodsItemId: p.goodsItemId || goods.find(g => g.name === p.name)?.id || idx + 1,
          name: p.name,
          type: p.type || "",
          deliveredQty: Number(p.deliveredQty) || 0,
          receivedQty: Number(p.receivedQty || p.qty),
          srNo: p.srNo || [],
          batchNo: p.selectedBatch ? [p.selectedBatch] : [],
        })),
    };

    try {
      console.log("ReceivedChallan - Submitting payload:", payload);
      
      // Validate that all products have required fields
      const invalidProducts = payload.items.filter(item => !item.name || !item.goodsItemId);
      if (invalidProducts.length > 0) {
        toast.error("Please ensure all products have valid names and are selected from the dropdown");
        return;
      }
      
      // Validate that at least one product has received quantity
      const productsWithReceivedQty = payload.items.filter(item => item.receivedQty > 0);
      if (productsWithReceivedQty.length === 0) {
        toast.error("Please enter received quantity for at least one product");
        return;
      }
      console.log("ReceivedChallan - Making API call to:", "challans/create");
      console.log("ReceivedChallan - Full URL:", "https://ctmapi.zerlak.com/api/v1/challans/create");
      console.log("ReceivedChallan - Payload being sent:", JSON.stringify(payload, null, 2));
      
      const response = await api.post("challans/create", payload);
      console.log("ReceivedChallan - API Response status:", response.status);
      console.log("ReceivedChallan - API Response data:", response.data);
      console.log("ReceivedChallan - Full API Response:", response);
      
      if (response.status === 201 || response.status === 200) {
        toast.success("Received Challan created successfully!");
        handleReset();
        // Refresh customer inventory after successful save
        if (form.customerId) {
          await fetchCustomerInventory(form.customerId);
        }
      } else {
        toast.error("Unexpected response status: " + response.status);
      }
    } catch (err) {
      console.error("ReceivedChallan - API Error:", err);
      console.error("ReceivedChallan - Error Response:", err.response?.data);
      
      // Handle specific error cases
      if (err.response?.status === 400) {
        const errorMessage = err.response?.data?.message || "Bad request - please check your data";
        toast.error("Validation Error: " + errorMessage);
      } else if (err.response?.status === 401) {
        toast.error("Authentication failed. Please login again.");
        navigate("/");
      } else if (err.response?.status === 403) {
        toast.error("You don't have permission to create challans.");
      } else if (err.response?.status === 404) {
        toast.error("API endpoint not found. Please contact support.");
      } else {
        toast.error("Error: " + (err.response?.data?.message || err.message || "Failed to save challan"));
      }
    }
  }

  const handleReset = () => {
    setForm({
      challanNumber: "",
      customer: "",
      customerId: "",
      challanType: "Received Challan",
      date: new Date().toISOString().split("T")[0],
      siteLocation: "",
      vehicleNo: "",
      transporter: "",
      driverDetails: "",
      deliveryDetails: "",
      poNo: "",
      depositType: "Deposit",
      depositAmount: "",
      depositNarration: "",
      products: [],
    });

    setProductForm({
      product: "",
      delivered: "",
      received: "",
      goodsItemId: null,
    });

    setShowProductForm(false);
    // Clear field errors
    setFieldErrors({
      customer: false,
      siteLocation: false,
      vehicleNo: false,
      transporter: false,
      products: false,
    });
  };

  // Additional functions for new product management
  const addProductRow = () => {
    const newProduct = {
      id: Date.now(),
      name: "",
      type: "",
      deliveredQty: "",
      receivedQty: "",
      srNo: ""
    };
    setForm(prev => ({ ...prev, products: [...prev.products, newProduct] }));
  };

  const removeProductRow = (index) => {
    if (form.products.length > 1) {
      const updatedProducts = form.products.filter((_, i) => i !== index);
      setForm(prev => ({ ...prev, products: updatedProducts }));
    }
  };

  const handleProductRowChange = (index, field, value) => {
    const updatedProducts = [...form.products];
    updatedProducts[index][field] = value;
    setForm(prev => ({ ...prev, products: updatedProducts }));
  };

  const handleDeleteProduct = (index) => {
    const updatedProducts = form.products.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, products: updatedProducts }));
  };

  const handleSaveChallan = (e) => {
    console.log("ReceivedChallan - Save Challan button clicked");
    // Create a synthetic form event and call handleSubmit to actually create the challan
    const syntheticEvent = {
      preventDefault: () => {}
    };
    handleSubmit(syntheticEvent);
  };

  // Debug function to test payload structure
  const debugPayload = () => {
    const payload = {
      customerId: form.customerId,
      customerName: form.customer,
      siteLocation: form.siteLocation,
      vehicleNumber: form.vehicleNo,
      deliveredChallanNo: "",
      driverName: form.driverDetails,
      driverNumber: "",
      transporter: form.transporter,
      lrNumber: "",
      challanNumber: form.challanNumber === "AUTO" ? "" : form.challanNumber,
      challanType: "RECEIVED",
      date: form.date,
      purchaseOrderNo: form.poNo,
      returnedAmount:
        form.depositType === "Returned" ? Number(form.depositAmount) : 0,
      depositeNarration: form.depositNarration,
      deliveryDetails: form.deliveryDetails,

      items: form.products
        .filter((p) => (p.receivedQty || p.qty) > 0)
        .map((p, idx) => ({
          goodsItemId: p.goodsItemId || goods.find(g => g.name === p.name)?.id || idx + 1,
          name: p.name,
          type: p.type || "",
          deliveredQty: Number(p.deliveredQty) || 0,
          receivedQty: Number(p.receivedQty || p.qty),
          srNo: p.srNo || [],
          batchNo: p.selectedBatch ? [p.selectedBatch] : [],
        })),
    };
    
    console.log("Debug - Current form state:", form);
    console.log("Debug - Payload that will be sent:", payload);
    console.log("Debug - Available goods:", goods);
    
    // Check for missing goodsItemId
    const missingIds = payload.items.filter(item => !item.goodsItemId);
    if (missingIds.length > 0) {
      console.warn("Debug - Items missing goodsItemId:", missingIds);
    }
    
    return payload;
  };

  // Mock ProductTable component (you may need to import or create this)
  const ProductTable = ({ prefix, pageType, products, onUpdate, onRemove }) => {
    // Add state for product search dropdowns
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdowns, setShowProductDropdowns] = useState({});
    const [filteredGoods, setFilteredGoods] = useState({});
    const [showBatchDropdowns, setShowBatchDropdowns] = useState({});
    const [showSrnoDropdowns, setShowSrnoDropdowns] = useState({});

    const handleProductSearch = (index, searchTerm) => {
      setProductSearchTerms(prev => ({ ...prev, [index]: searchTerm }));
      
      if (searchTerm.length < 2) {
        setFilteredGoods(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
        return;
      }

      const filtered = goods.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredGoods(prev => ({ ...prev, [index]: filtered }));
      setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
    };

    const handleProductSelect = async (index, selectedGood) => {
      // Check if product already exists in other rows
      const existingProductIndex = products.findIndex((p, i) => i !== index && p.name === selectedGood.name);
      if (existingProductIndex !== -1) {
        toast.warning("Product already added in another row");
        return;
      }
      
      onUpdate(index, 'name', selectedGood.name);
      onUpdate(index, 'goodsItemId', selectedGood.id);
      // Auto-fill type if available from goods data
      if (selectedGood && selectedGood.type) {
        onUpdate(index, 'type', selectedGood.type);
      }
      
      // Auto-fill delivered qty from delivered challans
      const deliveredQty = getDeliveredQtyForProduct(selectedGood.name);
      console.log("Auto-filling delivered qty:", deliveredQty, "for product:", selectedGood.name);
      if (deliveredQty) {
        onUpdate(index, 'deliveredQty', deliveredQty);
      }
      
      setProductSearchTerms(prev => ({ ...prev, [index]: selectedGood.name }));
      setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
      
      // Fetch cylinder data when product is selected
      try {
        const cylinderName = selectedGood.name; 
        const response = await api.get(`cylinderMaster/getSrno?cylinderName=${encodeURIComponent(cylinderName)}`);
        console.log("Cylinder data fetched:", response.data);
        
        // Store cylinder data for this product row
        if (response.data && Array.isArray(response.data)) {
          onUpdate(index, 'cylinderData', response.data);
          // Auto-select the first batch and srno
          if (response.data.length > 0) {
            onUpdate(index, 'selectedBatch', response.data[0].batchNo);
            onUpdate(index, 'selectedSrno', response.data[0].srno);
          }
        }
      } catch (error) {
        console.error("Error fetching cylinder data:", error);
        toast.error("Failed to fetch cylinder data");
      }
    };

    const setShowBatchDropdown = (index, show) => {
      setShowBatchDropdowns(prev => ({ ...prev, [index]: show }));
    };

    const setShowSrnoDropdown = (index, show) => {
      setShowSrnoDropdowns(prev => ({ ...prev, [index]: show }));
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        const batchContainers = document.querySelectorAll('.batch-srno-container');
        batchContainers.forEach((container, index) => {
          if (!container.contains(event.target)) {
            setShowBatchDropdowns(prev => ({ ...prev, [index]: false }));
          }
        });
        
        const srnoContainers = document.querySelectorAll('.srno-container');
        srnoContainers.forEach((container, index) => {
          if (!container.contains(event.target)) {
            setShowSrnoDropdowns(prev => ({ ...prev, [index]: false }));
          }
        });
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get delivered qty for a product from delivered challans
    const getDeliveredQtyForProduct = (productName) => {
      if (!deliveredChallans.length || !productName) return '';
      
      let totalDeliveredQty = 0;
      
      console.log("Calculating delivered qty for:", productName);
      console.log("Delivered challans:", deliveredChallans);
      
      deliveredChallans.forEach(challan => {
        if (challan.items && Array.isArray(challan.items)) {
          challan.items.forEach(item => {
            console.log("Checking item:", item.name, "vs", productName, "deliveredQty:", item.deliveredQty);
            if (item.name === productName && item.deliveredQty) {
              totalDeliveredQty += Number(item.deliveredQty);
              console.log("Added:", item.deliveredQty, "Total now:", totalDeliveredQty);
            }
          });
        }
      });
      
      console.log("Final total delivered qty:", totalDeliveredQty);
      return totalDeliveredQty > 0 ? totalDeliveredQty.toString() : '';
    };

    return (
      <div className="product-table">
        {products.map((product, index) => (
          <div key={product.id || index} className="product-row product-row-responsive">
            <div className="product-search-container">
              <label>Product Name</label>
              <div className="product-search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search product..."
                  value={productSearchTerms[index] || product.name || ''}
                  onChange={(e) => handleProductSearch(index, e.target.value)}
                  onFocus={() => {
                    const searchTerm = productSearchTerms[index] || product.name || '';
                    if (searchTerm.length >= 2) {
                      setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowProductDropdowns(prev => ({ ...prev, [index]: false })), 200)}
                  className="product-search-input"
                />
              </div>
              {showProductDropdowns[index] && (
                <div className="product-dropdown">
                  {filteredGoods[index]?.length === 0 && (productSearchTerms[index] || '').length >= 2 ? (
                    <div className="product-no-results">
                      <div className="no-results-icon">°</div>
                      <div className="no-results-text">No products found</div>
                      <div className="no-results-hint">Try searching with different keywords</div>
                    </div>
                  ) : (
                    (filteredGoods[index] || goods).map((item) => (
                      <div
                        key={item.id}
                        className="product-option"
                        onClick={() => handleProductSelect(index, item)}
                      >
                        <div className="product-option-main">
                          <div className="product-name">
                            {item.name}
                          </div>
                          {item.type && (
                            <div className="product-type">
                              Type: {item.type}
                            </div>
                          )}
                        </div>
                        <div className="product-select-icon">°</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <div>
              <label>Type</label>
              <input
                type="text"
                placeholder="Type"
                value={product.type}
                onChange={(e) => onUpdate(index, 'type', e.target.value)}
                className="product-input-responsive"
                style={{
                  background: "white",
                  cursor: "text"
                }}
              />
            </div>
            <div>
              <label>Delivered Qty</label>
              <input
                type="number"
                placeholder="Delivered Qty"
                value={product.deliveredQty || ''}
                onChange={(e) => onUpdate(index, 'deliveredQty', e.target.value)}
                disabled={true}
                className="product-input-responsive"
                style={{
                  background: "#f3f4f6",
                  cursor: "not-allowed",
                  color: "#6b7280"
                }}
                readOnly
              />
            </div>
            <div>
              <label>Received Qty</label>
              <input
                type="number"
                placeholder="Received Qty"
                value={product.receivedQty}
                onChange={(e) => onUpdate(index, 'receivedQty', e.target.value)}
                className="product-input-responsive"
              />
            </div>
            <div className="batch-srno-container batch-srno-responsive">
              <label>Batch No</label>
              <input
                type="text"
                placeholder="Batch No"
                value={product.selectedBatch || ''}
                onChange={(e) => onUpdate(index, 'selectedBatch', e.target.value)}
                onFocus={() => setShowBatchDropdown(index, true)}
                readOnly={product.cylinderData && product.cylinderData.length > 0}
                className="product-input-responsive"
                style={{
                  background: product.cylinderData && product.cylinderData.length > 0 ? "#f3f4f6" : "white",
                  cursor: product.cylinderData && product.cylinderData.length > 0 ? "pointer" : "text"
                }}
              />
              {showBatchDropdowns[index] && product.cylinderData && product.cylinderData.length > 0 && (
                <div className="batch-dropdown">
                  {product.cylinderData.map((cylinder, idx) => (
                    <div
                      key={idx}
                      className="batch-option"
                      onClick={() => {
                        onUpdate(index, 'selectedBatch', cylinder.batchNo);
                        onUpdate(index, 'selectedSrno', cylinder.srno);
                        setShowBatchDropdowns(prev => ({ ...prev, [index]: false }));
                      }}
                    >
                      <div className="batch-info">
                        <div className="batch-number">{cylinder.batchNo}</div>
                        <div className="sr-number">{cylinder.srno}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="srno-container srno-responsive">
              <label>Sr No</label>
              <input
                type="text"
                placeholder="Sr No"
                value={product.selectedSrno || product.srNo || ''}
                onChange={(e) => onUpdate(index, 'selectedSrno', e.target.value)}
                onFocus={() => setShowSrnoDropdown(index, true)}
                readOnly={product.cylinderData && product.cylinderData.length > 0}
                className="product-input-responsive"
                style={{
                  background: product.cylinderData && product.cylinderData.length > 0 ? "#f3f4f6" : "white",
                  cursor: product.cylinderData && product.cylinderData.length > 0 ? "pointer" : "text"
                }}
              />
              {showSrnoDropdowns[index] && product.cylinderData && product.cylinderData.length > 0 && (
                <div className="srno-dropdown">
                  {product.cylinderData.map((cylinder, idx) => (
                    <div
                      key={idx}
                      className="srno-option"
                      onClick={() => {
                        onUpdate(index, 'selectedSrno', cylinder.srno);
                        onUpdate(index, 'selectedBatch', cylinder.batchNo);
                        setShowSrnoDropdowns(prev => ({ ...prev, [index]: false }));
                      }}
                    >
                      <div className="srno-info">
                        <div className="srno-number">{cylinder.srno}</div>
                        <div className="batch-display">Batch: {cylinder.batchNo}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => products.length > 1 && onRemove(index)}
              disabled={products.length <= 1}
              style={{
                background: products.length <= 1 ? "#d1d5db" : "#ff4d4f",
                border: "none",
                color: products.length <= 1 ? "#9ca3af" : "#fff",
                padding: "5px 10px",
                borderRadius: "6px",
                cursor: products.length <= 1 ? "not-allowed" : "pointer"
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    );
  };

  
  return (
    <div className="rc-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="rc-card">
        <div className="rc-header">
          <div className="rc-header-left">
            <FaFileAlt className="rc-icon" />
            <h2>Received Challan Entry</h2>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rc-grid">
            {/* ================= CUSTOMER & LOCATION ================= */}
            <div className="rc-section">
              <div className="rc-section-title">Customer & Location</div>
              <div className="rc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="rc-field full">
                  <label>Customer Name *</label>
                  <div className="customer-search-container">
                    <input
                      name="customer"
                      value={customerSearchTerm || form.customer}
                      onChange={(e) => {
                        handleCustomerSearch(e.target.value);
                        // Clear error when user starts typing
                        if (fieldErrors.customer) {
                          setFieldErrors(prev => ({ ...prev, customer: false }));
                        }
                      }}
                      placeholder="Search customer name..."
                      required
                      style={{
                        border: fieldErrors.customer ? '2px solid #ef4444' : '1px solid #e2e8f0',
                        backgroundColor: fieldErrors.customer ? '#fef2f2' : 'white'
                      }}
                    />
                    {fieldErrors.customer && (
                      <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                        Please select a customer from the dropdown
                      </div>
                    )}
                    {showCustomerDropdown && customers.length > 0 && (
                      <div className="customer-dropdown">
                        {customers.map((customer) => (
                          <div
                            key={customer.id}
                            className="customer-option"
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            {customer.name || customer.customerName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rc-field full">
                  <label>Site Location *</label>
                  <input
                    name="siteLocation"
                    value={form.siteLocation}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.siteLocation) {
                        setFieldErrors(prev => ({ ...prev, siteLocation: false }));
                      }
                    }}
                    placeholder="Enter site location"
                    required
                    style={{
                      border: fieldErrors.siteLocation ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.siteLocation ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.siteLocation && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Site location is required
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ================= BASIC INFO ================= */}
            <div className="rc-section">
              <div className="rc-section-title">Basic Information</div>
              <div className="rc-grid" style={{ padding: 0, gap: '20px', gridTemplateColumns: '1fr' }}>
                <div className="rc-field">
                  <label>Date *</label>
                  <input
                    name="date"
                    type="date"
                    value={form.date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="rc-field">
                  <label>Challan Type</label>
                  <input
                    name="challanType"
                    value={form.challanType}
                    disabled
                    style={{
                      background: "#f3f4f6",
                      cursor: "not-allowed",
                      color: "#6b7280"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* ================= TRANSPORT DETAILS ================= */}
            <div className="rc-section">
              <div className="rc-section-title">Transport Details</div>
              <div className="rc-grid" style={{ padding: 0, gap: '20px', gridTemplateColumns: '1fr' }}>
                <div className="rc-field">
                  <label>Vehicle Number *</label>
                  <input
                    name="vehicleNo"
                    value={form.vehicleNo}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.vehicleNo) {
                        setFieldErrors(prev => ({ ...prev, vehicleNo: false }));
                      }
                    }}
                    placeholder="Enter vehicle number"
                    required
                    style={{
                      border: fieldErrors.vehicleNo ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.vehicleNo ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.vehicleNo && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Vehicle number is required
                    </div>
                  )}
                </div>

                <div className="rc-field">
                  <label>Transporter *</label>
                  <input
                    name="transporter"
                    value={form.transporter}
                    onChange={(e) => {
                      handleChange(e);
                      // Clear error when user starts typing
                      if (fieldErrors.transporter) {
                        setFieldErrors(prev => ({ ...prev, transporter: false }));
                      }
                    }}
                    placeholder="Enter transporter details"
                    required
                    style={{
                      border: fieldErrors.transporter ? '2px solid #ef4444' : '1px solid #e2e8f0',
                      backgroundColor: fieldErrors.transporter ? '#fef2f2' : 'white'
                    }}
                  />
                  {fieldErrors.transporter && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      Transporter is required
                    </div>
                  )}
                </div>

                <div className="rc-field">
                  <label>Driver Details</label>
                  <input
                    name="driverDetails"
                    value={form.driverDetails}
                    onChange={handleChange}
                    placeholder="Name Mobile (e.g., John 9876543210)"
                  />
                </div>
              </div>
            </div>

            {/* ================= DELIVERY INFO ================= */}
            <div className="rc-section">
              <div className="rc-section-title">Delivery Information</div>
              <div className="rc-grid" style={{ padding: 0, gap: '20px', gridTemplateColumns: '1fr' }}>
                <div className="rc-field">
                  <label>Delivery Details</label>
                  <input
                    name="deliveryDetails"
                    value={form.deliveryDetails}
                    onChange={handleChange}
                    placeholder="Enter delivery details"
                  />
                </div>

                <div className="rc-field">
                  <label>PO No.</label>
                  <input
                    name="poNo"
                    value={form.poNo}
                    onChange={handleChange}
                    placeholder="Enter PO number"
                  />
                </div>

                <div className="rc-field">
                  <label>Challan Number</label>
                  <input
                    name="challanNumber"
                    value={form.challanNumber}
                    onChange={handleChange}
                    placeholder="Enter challan number (optional)"
                  />
                </div>
              </div>
            </div>

            {/* ================= DEPOSIT INFO ================= */}
            <div className="rc-section full-width">
              <div className="rc-section-title">Deposit Information</div>
              <div className="rc-grid" style={{ padding: 0, gap: '20px' }}>
                <div className="rc-field">
                  <label>Deposit Type</label>
                  <select
                    name="depositType"
                    value={form.depositType}
                    onChange={handleChange}
                  >
                    <option value="Deposit">Deposit</option>
                    <option value="Returned">Returned</option>
                  </select>
                </div>

                <div className="rc-field">
                  <label>Deposit Amount</label>
                  <input
                    name="depositAmount"
                    type="number"
                    value={form.depositAmount}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="rc-field full">
                  <label>Deposit Narration</label>
                  <input
                    name="depositNarration"
                    value={form.depositNarration}
                    onChange={handleChange}
                    placeholder="Enter narration"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Add Product Button - Show when no products section */}
          {!showProductsSection && (
            <div className="dc-section">
              <div className="dc-product-add-row">
                <button
                  type="button"
                  className="dc-btn-primary-modern"
                  onClick={() => {
                    setShowProductsSection(true);
                    const newProduct = { id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: "", goodsItemId: null };
                    setForm(prev => ({ ...prev, products: [newProduct] }));
                  }}
                >
                  <span className="dc-btn-icon">+</span>
                  Add Product
                </button>
              </div>
            </div>
          )}

          {/* Products Section - Show only when user clicks Add Product */}
          {showProductsSection && (
            <div className="dc-section">
              <div className="dc-section-title">Products</div>
              
              {/* Product Empty State */}
              {form.products.length === 0 && (
                <div className="dc-product-empty-state" style={{
                  border: fieldErrors.products ? '2px solid #ef4444' : '1px dashed #d1d5db',
                  backgroundColor: fieldErrors.products ? '#fef2f2' : '#f9fafb',
                  padding: '20px',
                  textAlign: 'center',
                  borderRadius: '8px'
                }}>
                  <p style={{ 
                    color: fieldErrors.products ? '#ef4444' : '#6b7280',
                    margin: '0 0 15px 0'
                  }}>
                    {fieldErrors.products ? 'At least one product is required' : 'No products added yet'}
                  </p>
                  <button
                    type="button"
                    className="dc-btn-primary-modern"
                    onClick={() => {
                      const newProduct = { id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: "", goodsItemId: null };
                      setForm(prev => ({ ...prev, products: [newProduct] }));
                      // Clear error when user adds a product
                      if (fieldErrors.products) {
                        setFieldErrors(prev => ({ ...prev, products: false }));
                      }
                    }}
                  >
                    <span className="dc-btn-icon">+</span>
                    Add First Product
                  </button>
                </div>
              )}

              {/* Product Table */}
              {form.products.length > 0 && (
                <div className="dc-product-table-responsive">
                  <ProductTable
                    prefix="rc"
                    pageType="received"
                    products={form.products}
                    onUpdate={handleProductRowChange}
                    onRemove={removeProductRow}
                  />
                  <div className="dc-product-add-row">
                    <button
                      type="button"
                      className="dc-btn-primary-modern"
                      onClick={() => {
                        const newProduct = { id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: "", goodsItemId: null };
                        setForm(prev => ({ ...prev, products: [...prev.products, newProduct] }));
                        // Clear error when user adds a product
                        if (fieldErrors.products) {
                          setFieldErrors(prev => ({ ...prev, products: false }));
                        }
                      }}
                    >
                      <span className="dc-btn-icon">+</span>
                      Add Product
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="dc-actions">
                    <button
                      type="button"
                      className="dc-btn-secondary"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="dc-btn-primary"
                      onClick={handleSaveChallan}
                    >
                      Save Challan
                    </button>
                    <button
                      type="submit"
                      className="dc-btn-primary"
                    >
                      Submit
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>

      {/* Extended Product Form - Inline */}
      {showExtendedProductForm && (
        <div className="rc-extended-product-section" ref={extendedFormRef}>
          <div className="rc-extended-product-header">
            <h3><FaBox /> Add Multiple Products</h3>
            <button 
              className="rc-extended-close-btn"
              onClick={() => setShowExtendedProductForm(false)}
            >
              ×
            </button>
          </div>
          <div className="rc-extended-product-content">
            <InsertProduct
              onProductsAdded={handleProductsAdded}
              onClose={() => setShowExtendedProductForm(false)}
              isInModal={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ReceivedChallan;
